import type { IdentityV1 } from '@alpod-ai-writer/backend';
import { BrainCircuit, Coins, CreditCard, FileText, LayoutDashboard, LayoutTemplate, ListTodo, Package, Receipt, Ticket, Users, type LucideIcon } from 'lucide-react';
import { NavLink } from 'react-router';

import { type AdminPrivilegeKey, canRead } from '@/auth/useAdminAccess';
import { ProductLogo } from '@/components/ProductLogo';
import type { TranslationKey } from '@/i18n/translations';
import { useI18n } from '@/i18n/useI18n';
import { cn } from '@/lib/utils';

import { SIDEBAR_WIDTH_CLASS } from './layout.constants';

interface NavItem {
    labelKey: TranslationKey;
    icon: LucideIcon;
    path: string;
    privilege: AdminPrivilegeKey | null;
}

const navItems: NavItem[] = [
    { labelKey: 'nav.dashboard', icon: LayoutDashboard, path: '/', privilege: null },
    { labelKey: 'nav.users', icon: Users, path: '/users', privilege: 'Identities' },
    { labelKey: 'nav.documents', icon: FileText, path: '/documents', privilege: 'Documents' },
    { labelKey: 'nav.templates', icon: LayoutTemplate, path: '/templates', privilege: 'Templates' },
    { labelKey: 'nav.creditPackages', icon: Package, path: '/credit-packages', privilege: null },
    { labelKey: 'nav.creditSettings', icon: Coins, path: '/credit-settings', privilege: null },
    { labelKey: 'nav.vouchers', icon: Ticket, path: '/vouchers', privilege: null },
    { labelKey: 'nav.paymentOrders', icon: Receipt, path: '/payment-orders', privilege: null },
    { labelKey: 'nav.jobQueue', icon: ListTodo, path: '/job-queue', privilege: 'JobQueue' },
    { labelKey: 'nav.aiUsage', icon: BrainCircuit, path: '/ai-usage', privilege: 'AIUsage' },
    { labelKey: 'nav.documentUnlockSettings', icon: CreditCard, path: '/document-unlock-settings', privilege: null },
];

interface SidebarProps {
    identity: IdentityV1.OwnRecord;
}

export function Sidebar({ identity }: SidebarProps) {
    const { t } = useI18n();

    const visibleItems = navItems.filter(item => {
        if (!item.privilege) return true;
        return canRead(identity, item.privilege);
    });

    return (
        <aside className={cn('fixed left-0 top-0 z-30 flex h-screen flex-col border-r bg-sidebar-background', SIDEBAR_WIDTH_CLASS)}>
            <div className="flex h-16 items-center border-b px-6">
                <ProductLogo />
            </div>

            <nav className="flex-1 space-y-1 p-3">
                {visibleItems.map((item) => (
                    <NavLink
                        key={item.path}
                        to={item.path}
                        end={item.path === '/'}
                        className={({ isActive }) =>
                            cn(
                                'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                                isActive
                                    ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                                    : 'text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                            )
                        }
                    >
                        <item.icon className="h-4 w-4" />
                        {t(item.labelKey)}
                    </NavLink>
                ))}
            </nav>
        </aside>
    );
}
